import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';
import AnimatedCloseButton from './AnimatedCloseButton';

interface AdditionalNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  notes: any[];
  title?: string;
}

export default function AdditionalNotesModal({ 
  isOpen, 
  onClose, 
  notes, 
  title = "Onay Ayrıntısı" 
}: AdditionalNotesModalProps) {
  // Scroll engelleyici hook kullan
  useScrollPrevention(isOpen);

  // Modal geri düğmesi handler
  const handleModalClose = useCallback(() => {
    onClose();
  }, [onClose]);
  
  useModalBackHandler({ 
    isOpen, 
    onClose: handleModalClose, 
    enabled: true
  });
  
  // ESC tuşu ile modal'ı kapatma
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);



  // HTML'den temiz metin çıkarma ve boş olup olmadığını kontrol etme
  const hasVisibleText = (htmlString: string | null | undefined): boolean => {
    if (!htmlString) return false;
    
    // HTML taglarını kaldır
    const textContent = htmlString.replace(/<[^>]*>/g, '').trim();
    
    // Boş string, sadece whitespace veya &nbsp; gibi HTML entity'leri kontrol et
    return textContent.length > 0 && textContent !== '&nbsp;';
  };

  // Ek notların varlığını kontrol etme
  const hasAdditionalNotes = notes && notes.length > 0 && 
    notes.some(note => note.Notlar && hasVisibleText(note.Notlar));

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 overflow-y-auto bg-black/20 dark:bg-black/40 backdrop-blur-sm"
          onClick={onClose}
        >
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative transform overflow-hidden rounded-xl backdrop-blur-xs bg-gradient-to-br from-white/90 via-white/88 to-white/85 dark:from-gray-700/85 dark:via-gray-750/83 dark:to-gray-800/85 border border-white/50 dark:border-gray-600/50 text-left shadow-2xl shadow-black/10 dark:shadow-black/30 transition-all w-full max-w-2xl max-h-[70vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-300 dark:border-gray-600">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {title}
                </h3>
                <AnimatedCloseButton 
                  onClick={onClose}
                  size="sm"
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-200"
                />
              </div>
              
              <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(70vh-120px)]">
                {hasAdditionalNotes ? (
                  notes
                    .filter(note => note.Notlar && hasVisibleText(note.Notlar))
                    .map((note, index) => (
                                             <div key={index} className="bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600 border-l-4 border-blue-400 p-3 rounded border border-indigo-100 dark:border-gray-600">
                        <div 
                          className="text-sm text-blue-700 dark:text-blue-300 prose prose-sm dark:prose-invert max-w-none"
                          dangerouslySetInnerHTML={{ __html: note.Notlar || '' }}
                        />
                        {note.Tarih && (
                          <div className="text-xs text-blue-600 dark:text-blue-400 mt-2 opacity-75">
                            {new Date(note.Tarih).toLocaleDateString('tr-TR')}
                          </div>
                        )}
                      </div>
                    ))
                ) : (
                  <div className="text-center py-8 bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-600 dark:via-gray-600/90 dark:to-gray-500 border border-gray-200 dark:border-gray-500 rounded-full p-4 mb-4 w-16 h-16 mx-auto flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">
                      Henüz ek not bulunmamaktadır.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
} 